import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Form, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import ReactDatePicker from 'react-datepicker';
import { resourceCache } from 'use-async-resource';
import { toast } from 'react-toastify';
import dateFormat from 'dateformat';
import {
    AddProps,
    ClientSelectItem,
    MonthlyInvoiceExisting,
    MonthlyInvoiceRow,
} from '../models/table-models';
import {
    generateMonthlyInvoices,
    getInvoicePreview,
    getMonthlyInvoicePreview,
    searchClients,
    useGetInvoices,
} from '../utils/clients-hooks';
import { useGetAllTurnovers } from '../utils/users-hooks';
import { fetchTasksAll } from '../utils/dashboard-hooks';
import { CustomToastContent } from '../components/CustomToastContent';
import { toastOptionsTopRight } from '../utils/Enums/ToastOptions';
import { getCurrencySymbol } from '../utils/helper-functions';
import { useOpenBranchCard } from '../utils/useOpenBranchCard';
import { NewRequestContext } from '../context/NewRequestContext';

/** Стойностите на `ClientPaymentType` в бекенда - платежният документ на обекта. */
const DOCUMENT_TYPES = ['INVOICE', 'RECEIPT'];

/**
 * Стойностите на `ClientPaymentMethod`. Картата влиза в списъка, макар да не се среща често: без нея
 * обектите с този начин на плащане не биха съвпаднали с никоя отметка и биха изчезнали от списъка тихо.
 */
const PAYMENT_METHODS = ['CASH', 'BANK', 'CARD'];

/**
 * Екранът се отваря върху фактура по банка, защото това е ежедневната работа. Стокови разписки и плащания
 * в брой се виждат с отмятане - тогава излизат и обектите без платежни данни, при които полето е празно.
 */
const DEFAULT_DOCUMENT_TYPES = ['INVOICE'];
const DEFAULT_PAYMENT_METHODS = ['BANK'];

/** Под две букви не се пита, а над петдесет реда не се искат. Клиентите на прода са единайсет хиляди. */
const MIN_CLIENT_SEARCH_LENGTH = 2;
const MAX_CLIENT_SEARCH_RESULTS = 50;
const CLIENT_SEARCH_DEBOUNCE_MS = 250;

/** Клиент в падащия списък. `companyName` се пази отделно от надписа, който носи и ЕИК-а. */
interface ClientOption {
    value: string;
    name: string;
    companyName: string;
}

const toClientOption = ({ value, name, eik }: ClientSelectItem): ClientOption => ({
    value: String(value),
    name: eik ? `${name} (${eik})` : name,
    companyName: name,
});

/**
 * Отметките са филтър само докато не са всички: с пълния избор редът минава, каквото и да пише в него.
 * Иначе обектите без платежни данни, при които полето е празно, биха изчезнали още при отварянето -
 * а точно те са тези, които офисът трябва да види, за да ги оправи.
 */
const matchesFilter = (value: string | null, selected: string[], all: string[]) =>
    selected.length === all.length || (value != null && selected.includes(value));

const toggleValue = (selected: string[], value: string) =>
    selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value];

/**
 * Диалогът зад „Добави плащане" - месечно фактуриране на обектите на един клиент.
 *
 * Редът е месец -> клиент -> обекти, защото периодът на всеки обект се смята спрямо избрания месец и без
 * него списъкът няма какво да покаже. Периодът не е календарният месец: обект с отчетен ден 15 получава
 * 15.09 - 14.10 за септември, а обект с тримесечно предплащане - целия период, който покрива месеца.
 * Затова всеки ред показва периода, който ще влезе във фактурата.
 *
 * Сумите пътуват в стотинки и се делят само при показване. При генериране нагоре отиват само id-тата на
 * обектите - периодът и сумата се смятат наново на сървъра.
 */
export const AddMonthlyInvoices = ({
    setShowModal,
    refetchEntitiesHook,
    setIsLoading,
    setCompleteButtonDisabled,
    setSubmitLabel,
}: AddProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });
    const { selectedClient } = useContext(NewRequestContext);

    const [month, setMonth] = useState(dateFormat(new Date(), 'yyyy-mm'));
    const monthDate = useMemo(() => new Date(`${month}-01T00:00:00`), [month]);
    const [rows, setRows] = useState<MonthlyInvoiceRow[]>();
    const [loadingRows, setLoadingRows] = useState(false);
    const [documentTypes, setDocumentTypes] = useState<string[]>(DEFAULT_DOCUMENT_TYPES);
    const [paymentMethods, setPaymentMethods] = useState<string[]>(DEFAULT_PAYMENT_METHODS);
    // Пази се свалената отметка, не сложената: така прибирането и връщането на филтър не губи избора на
    // оператора - скритият обект се връща отметнат, вместо да остане тихо изпуснат.
    const [deselected, setDeselected] = useState<number[]>([]);
    const [submitting, setSubmitting] = useState(false);

    const [clientSearch, setClientSearch] = useState('');
    const [clientResults, setClientResults] = useState<ClientOption[]>([]);
    const [searchingClients, setSearchingClients] = useState(false);
    const [selectedClientOption, setSelectedClientOption] = useState<ClientOption | null>(null);
    const clientId = selectedClientOption ? Number(selectedClientOption.value) : undefined;

    const { open: openBranchCard, branchCard } = useOpenBranchCard();
    // Кой ред се отваря в момента - спинерът стои в самия него, а не някъде над таблицата.
    const [openingBranch, setOpeningBranch] = useState<number>();

    const openBranch = async (branchId: number) => {
        setOpeningBranch(branchId);
        try {
            await openBranchCard(branchId, selectedClientOption?.companyName);
        } finally {
            setOpeningBranch(undefined);
        }
    };

    /**
     * Търсенето е на сървъра. Дотук екранът дърпаше всичките единайсет хиляди клиента с обектите им от
     * `/clients/short` и отсяваше в браузъра - тежко и при отваряне, и на всеки клавиш. Сега пита
     * `/clients/selectItemsFilter`, който отсява в базата и връща най-много петдесет реда.
     *
     * Под две букви не се пита: при този брой клиенти една буква не отсява нищо. Изчакването гълта
     * бързото писане - иначе „Хотел" пуска пет заявки, от които четири остават без нужда.
     */
    useEffect(() => {
        const query = clientSearch.trim();
        if (query.length < MIN_CLIENT_SEARCH_LENGTH) {
            setClientResults([]);
            setSearchingClients(false);
            return;
        }

        let current = true;
        setSearchingClients(true);
        const timer = setTimeout(() => {
            searchClients(query, MAX_CLIENT_SEARCH_RESULTS)
                .then((items) => current && setClientResults(items.map(toClientOption)))
                .catch(() => current && setClientResults([]))
                .finally(() => current && setSearchingClients(false));
        }, CLIENT_SEARCH_DEBOUNCE_MS);

        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [clientSearch]);

    // Отваряне от картона на клиент: избран е наготово, за да не се търси втори път. Веднъж, защото после
    // отварянето на картон от някой ред слага свой клиент в контекста, а и изчистването трябва да остане.
    const prefilledClient = useRef(false);
    useEffect(() => {
        if (prefilledClient.current || selectedClientOption != null || !selectedClient?.id) return;
        prefilledClient.current = true;
        setSelectedClientOption({
            value: String(selectedClient.id),
            name: selectedClient.eik
                ? `${selectedClient.companyName} (${selectedClient.eik})`
                : selectedClient.companyName,
            companyName: selectedClient.companyName,
        });
    }, [selectedClient, selectedClientOption]);

    useEffect(() => {
        if (clientId == null || !month) {
            setRows(undefined);
            setDeselected([]);
            return;
        }

        let current = true;
        setLoadingRows(true);
        setDeselected([]);
        getMonthlyInvoicePreview(clientId, month)
            .then((result) => current && setRows(result))
            .catch(() => current && setRows([]))
            .finally(() => current && setLoadingRows(false));

        return () => {
            current = false;
        };
    }, [clientId, month]);

    const visibleRows = useMemo(
        () =>
            (rows ?? []).filter(
                (row) =>
                    matchesFilter(row.paymentType, documentTypes, DOCUMENT_TYPES) &&
                    matchesFilter(row.paymentMethod, paymentMethods, PAYMENT_METHODS)
            ),
        [rows, documentTypes, paymentMethods]
    );

    const selectableIds = useMemo(
        () => visibleRows.filter(({ selectable }) => selectable).map(({ branchId }) => branchId),
        [visibleRows]
    );

    const selectedIds = useMemo(
        () => selectableIds.filter((id) => !deselected.includes(id)),
        [selectableIds, deselected]
    );

    const toggle = (branchId: number) =>
        setDeselected((previous) =>
            previous.includes(branchId)
                ? previous.filter((id) => id !== branchId)
                : [...previous, branchId]
        );

    const toggleAll = () =>
        setDeselected((previous) =>
            selectedIds.length === selectableIds.length
                ? Array.from(new Set([...previous, ...selectableIds]))
                : previous.filter((id) => !selectableIds.includes(id))
        );

    const total = useMemo(
        () =>
            visibleRows
                .filter(({ branchId }) => selectedIds.includes(branchId))
                .reduce((sum, { periodFee }) => sum + periodFee, 0),
        [visibleRows, selectedIds]
    );

    const currency = rows?.find(({ currency: value }) => value)?.currency ?? undefined;

    useEffect(() => {
        setSubmitLabel && setSubmitLabel(t('generateDuty'));
        return () => setSubmitLabel && setSubmitLabel(undefined);
    }, [setSubmitLabel, t]);

    // Без избран обект бутонът няма какво да направи; заключен, той не мълчи, а си личи защо.
    useEffect(() => {
        setCompleteButtonDisabled && setCompleteButtonDisabled(selectedIds.length === 0);
        return () => setCompleteButtonDisabled && setCompleteButtonDisabled(false);
    }, [selectedIds, setCompleteButtonDisabled]);

    const submit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            if (clientId == null || selectedIds.length === 0 || submitting) return;

            setSubmitting(true);
            setIsLoading && setIsLoading(true);
            try {
                const result = await generateMonthlyInvoices(clientId, month, selectedIds);

                const messages = [t('dutiesCreated', { count: result.created.length })];
                if (result.skipped.length > 0) {
                    messages.push(t('dutiesSkipped', { count: result.skipped.length }));
                }
                toast.success(<CustomToastContent content={messages} />, toastOptionsTopRight);

                resourceCache(fetchTasksAll).clear();
                resourceCache(useGetInvoices).clear();
                resourceCache(useGetAllTurnovers).clear();
                resourceCache(getInvoicePreview).clear();
                refetchEntitiesHook();
                setShowModal(false);
            } catch {
                // Съобщението вече е показано от общия интерцептор. Диалогът остава отворен с избора си,
                // за да може операторът да опита пак, без да пренарежда чекбоксовете.
            } finally {
                setSubmitting(false);
                setIsLoading && setIsLoading(false);
            }
        },
        [clientId, month, selectedIds, submitting, refetchEntitiesHook, setShowModal, setIsLoading, t]
    );

    return (
        <Form id='form' className='d-flex flex-column p-4 w-100' noValidate onSubmit={submit}>
            <div className='mb-3'>
                <div className='py-1'>{t('invoiceMonth')}</div>
                {/*
                    Нарочно не е <input type="month">: Firefox не го поддържа и там полето става свободен
                    текст, в който операторът трябва да напише „2026-09" наум.
                */}
                <ReactDatePicker
                    selected={monthDate}
                    onChange={(date) => date && setMonth(dateFormat(date, 'yyyy-mm'))}
                    dateFormat='MM/yyyy'
                    showMonthYearPicker
                    onChangeRaw={(event) => event.preventDefault()}
                    popperPlacement='bottom-end'
                    className='border border-secondary text-start p-2 py-1 rounded'
                />
            </div>

            <div className='mb-3'>
                <div className='py-1'>{t('forClient')}</div>
                <Select<ClientOption>
                    className='w-100'
                    isClearable
                    isDisabled={!month}
                    value={selectedClientOption}
                    onChange={setSelectedClientOption}
                    placeholder={commonT('enterNameOrEik')}
                    maxMenuHeight={150}
                    getOptionValue={({ value }) => value}
                    getOptionLabel={({ name }) => name}
                    options={clientResults}
                    isLoading={searchingClients}
                    inputValue={clientSearch}
                    onInputChange={setClientSearch}
                    // Отсяването е в базата - нямаме пълния списък, по който react-select да филтрира.
                    filterOption={null}
                    noOptionsMessage={() =>
                        clientSearch.trim().length < MIN_CLIENT_SEARCH_LENGTH
                            ? t('typeToSearchClient', { count: MIN_CLIENT_SEARCH_LENGTH })
                            : t('noClientsFound')
                    }
                    // Списъкът излиза в body, за да не го реже диалогът, но там подразбиращият се
                    // z-index е под този на модала (2010) - без стила падаше зад диалога.
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                />
            </div>

            {loadingRows && (
                <div>
                    <Spinner animation='border' /> {t('LoadingBranches')}
                </div>
            )}

            {!loadingRows && rows != null && rows.length === 0 && (
                <Alert variant='warning'>{t('noBranchesForClient')}</Alert>
            )}

            {!loadingRows && rows != null && rows.length > 0 && (
                <>
                    <div className='d-flex flex-wrap gap-4 mb-3'>
                        <FilterGroup
                            label={t('paymentType')}
                            options={DOCUMENT_TYPES}
                            selected={documentTypes}
                            onToggle={(value) => setDocumentTypes(toggleValue(documentTypes, value))}
                        />
                        <FilterGroup
                            label={t('paymentMethod')}
                            options={PAYMENT_METHODS}
                            selected={paymentMethods}
                            onToggle={(value) => setPaymentMethods(toggleValue(paymentMethods, value))}
                        />
                    </div>

                    {visibleRows.length === 0 ? (
                        <Alert variant='warning'>{t('noBranchesForFilter')}</Alert>
                    ) : (
                        <>
                            <BootstrapTable className='w-100' hover>
                                <thead>
                                    <tr>
                                        <th style={{ width: '2.5rem' }}>
                                            <Form.Check
                                                type='checkbox'
                                                aria-label={t('selectAllBranches')}
                                                disabled={selectableIds.length === 0}
                                                checked={
                                                    selectableIds.length > 0 &&
                                                    selectedIds.length === selectableIds.length
                                                }
                                                onChange={toggleAll}
                                            />
                                        </th>
                                        <th>{commonT('branch')}</th>
                                        <th>{t('invoicePeriod')}</th>
                                        <th className='text-end'>{t('periodFee')}</th>
                                        <th>{t('alreadyIssued')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.map((row) => (
                                        <MonthlyRow
                                            key={row.branchId}
                                            row={row}
                                            checked={selectedIds.includes(row.branchId)}
                                            opening={openingBranch === row.branchId}
                                            onToggle={() => toggle(row.branchId)}
                                            onOpen={() => openBranch(row.branchId)}
                                        />
                                    ))}
                                </tbody>
                            </BootstrapTable>

                            <div className='d-flex justify-content-between fw-bold border-top pt-2'>
                                <span>{t('selectedBranchesCount', { count: selectedIds.length })}</span>
                                <span>
                                    {(total / 100).toFixed(2)} {getCurrencySymbol(currency ?? undefined)}
                                </span>
                            </div>
                        </>
                    )}
                </>
            )}

            {branchCard}
        </Form>
    );
};

const FilterGroup = ({
    label,
    options,
    selected,
    onToggle,
}: {
    label: string;
    options: string[];
    selected: string[];
    onToggle: (value: string) => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });

    return (
        <div>
            <div className='py-1'>{label}</div>
            <div className='d-flex gap-3'>
                {options.map((value) => (
                    <Form.Check
                        key={value}
                        type='checkbox'
                        id={`monthly-filter-${value}`}
                        label={t(value)}
                        checked={selected.includes(value)}
                        onChange={() => onToggle(value)}
                    />
                ))}
            </div>
        </div>
    );
};

const MonthlyRow = ({
    row,
    checked,
    opening,
    onToggle,
    onOpen,
}: {
    row: MonthlyInvoiceRow;
    checked: boolean;
    opening: boolean;
    onToggle: () => void;
    onOpen: () => void;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });

    const alreadyInvoiced = row.blockedReason === 'alreadyInvoiced';
    const period = `${formatDate(row.periodFrom)} - ${formatDate(row.periodTo)}`;

    return (
        <tr
            className={row.selectable ? undefined : 'text-muted'}
            style={{ cursor: 'pointer' }}
            title={t('openBranchCard')}
            onClick={onOpen}
        >
            {/*
                Отметката е върху реда, който сам отваря картона - натисната, тя не бива да го отваря
                и покрай това.
            */}
            <td onClick={(event) => event.stopPropagation()}>
                {/*
                    Вече издаденото задължение стои маркирано, но заковано: то покрива месеца и се брои
                    за включено, а втора фактура за същия период не се издава.
                */}
                <Form.Check
                    type='checkbox'
                    checked={row.selectable ? checked : alreadyInvoiced}
                    disabled={!row.selectable}
                    onChange={onToggle}
                />
            </td>
            <td>
                {row.branchName}
                {opening && <Spinner animation='border' size='sm' className='ms-2' />}
            </td>
            <td>
                {period}
                {row.invoicingMonths > 1 && (
                    <div className='small'>{t('prepaidMonths', { count: row.invoicingMonths })}</div>
                )}
            </td>
            <td className='text-end'>
                {(row.periodFee / 100).toFixed(2)} {getCurrencySymbol(row.currency ?? undefined)}
            </td>
            <td>
                {row.existingInvoices.length > 0 ? (
                    row.existingInvoices.map((invoice) => (
                        <div key={invoice.id} className='small'>
                            {t(existingInvoiceLabelKey(invoice), {
                                number:
                                    invoice.creditNoteNumber ??
                                    invoice.invoiceNumber ??
                                    invoice.documentNumber,
                                total: (invoice.total / 100).toFixed(2),
                            })}
                        </div>
                    ))
                ) : row.blockedReason ? (
                    <span className='small'>{t(`monthlyBlocked_${row.blockedReason}`)}</span>
                ) : null}
            </td>
        </tr>
    );
};

/**
 * Как да се нарече вече издаденото. Не всяко задължение е фактура с номер: стоковата разписка носи
 * номера си в друга колона, сторнираното - в трета, а прясно създаденото още няма никакъв. Дотук всичко
 * се изписваше като „Фактура № ", а при липсващ номер на негово място влизаше вътрешното id, което не
 * значи нищо за офиса и се чете като чужд номер.
 */
const existingInvoiceLabelKey = (invoice: MonthlyInvoiceExisting) => {
    if (invoice.creditNoteNumber != null) return 'willBeIncludedCreditNote';
    if (invoice.invoiceNumber != null) return 'willBeIncluded';
    if (invoice.documentNumber != null) return 'willBeIncludedDocument';
    return 'willBeIncludedNoNumber';
};

const formatDate = (value: string) => dateFormat(value, 'dd.mm.yyyy');
